import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Package, Shield, Zap, ArrowUp, ArrowDown, icons } from "lucide-react";
import { useLocale } from "@/hooks/useLocale";
import type { Translations } from "@/lib/i18n/types";

export interface ItemModifier {
  stat: string;          // e.g. "carry_capacity", "STR", "DEX", "armor_class", "max_hp"
  value: number;         // +10, -2, etc.
  type?: "flat" | "percent"; // flat additive or percentage
  description?: string;  // "Exoskeleton hydraulic assist"
}

export interface ItemData {
  name: string;
  weight?: number;
  quantity?: number;
  rarity?: "common" | "uncommon" | "rare" | "epic" | "legendary";
  category?: string;
  description?: string;
  image_url?: string | null;
  icon?: string;                // lucide icon name e.g. "Sword", "Flask", "Gem"
  // New fields
  equipped?: boolean;
  equip_slot?: string;          // "armor", "accessory", "weapon_main", "weapon_off", "ring", "amulet", "back", "belt"
  modifiers?: ItemModifier[];   // stat modifiers when equipped
  effects?: string[];           // passive effect descriptions: "Immunity to cold", "Night vision 30ft"
  durability?: number;          // 0-100, null = indestructible
  charges?: number;             // for consumables/wands: remaining charges
  max_charges?: number;
  is_quest_item?: boolean;      // can't be dropped
  is_cursed?: boolean;          // can't be unequipped
  set_id?: string;              // belongs to item set for set bonuses
}

/** Resolve a lucide icon name to a component, falling back to Package */
function ItemIcon({ name, className }: { name?: string; className?: string }) {
  if (!name) return <Package className={className} />;
  // Try PascalCase first, then kebab-to-pascal
  const pascal = name.replace(/(^|[-_ ])(\w)/g, (_, __, c) => c.toUpperCase());
  const Icon = (icons as any)[pascal] || (icons as any)[name];
  if (Icon) return <Icon className={className} />;
  return <Package className={className} />;
}

const RARITY_STYLES: Record<string, { border: string; glow: string; labelKey: keyof Translations; text: string }> = {
  common: { border: "border-muted-foreground/30", glow: "", labelKey: "play_rarity_common", text: "text-muted-foreground" },
  uncommon: { border: "border-green-500/60", glow: "shadow-[0_0_8px_hsl(142_70%_45%/0.2)]", labelKey: "play_rarity_uncommon", text: "text-green-500" },
  rare: { border: "border-blue-500/60", glow: "shadow-[0_0_8px_hsl(217_90%_60%/0.3)]", labelKey: "play_rarity_rare", text: "text-blue-500" },
  epic: { border: "border-purple-500/60", glow: "shadow-[0_0_12px_hsl(270_70%_60%/0.3)]", labelKey: "play_rarity_epic", text: "text-purple-500" },
  legendary: { border: "border-primary/80", glow: "shadow-[0_0_16px_hsl(var(--primary)/0.4)]", labelKey: "play_rarity_legendary", text: "text-primary" },
};

function ModifierBadge({ mod }: { mod: ItemModifier }) {
  const isPositive = mod.value > 0;
  const Icon = isPositive ? ArrowUp : ArrowDown;
  const suffix = mod.type === "percent" ? "%" : "";
  return (
    <span className={cn(
      "inline-flex items-center gap-0.5 text-[9px] font-medium px-1 py-0 rounded",
      isPositive ? "text-green-400 bg-green-500/10" : "text-red-400 bg-red-500/10",
    )}>
      <Icon className="w-2.5 h-2.5" />
      {isPositive ? "+" : ""}{mod.value}{suffix} {mod.stat}
    </span>
  );
}

export function ItemCard({ item }: { item: ItemData }) {
  const { t } = useLocale();
  const rarity = item.rarity || "common";
  const style = RARITY_STYLES[rarity] || RARITY_STYLES.common;
  const hasModifiers = item.modifiers && item.modifiers.length > 0;
  const hasEffects = item.effects && item.effects.length > 0;
  const hasCharges = item.charges != null && item.max_charges != null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "w-full text-left p-2 rounded-md border bg-card/80 transition-all hover:scale-[1.02] cursor-pointer",
            style.border,
            style.glow,
            item.equipped && "ring-1 ring-primary/40 bg-primary/5",
          )}
        >
          <div className="flex items-center gap-2">
            {/* Item thumbnail */}
            {item.image_url ? (
              <img
                src={item.image_url}
                alt={item.name}
                className={cn(
                  "w-8 h-8 rounded object-cover shrink-0 border",
                  style.border,
                )}
                loading="lazy"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
            ) : (
              <div className={cn(
                "w-8 h-8 rounded shrink-0 border flex items-center justify-center bg-muted/30",
                style.border,
              )}>
                <ItemIcon name={item.icon} className="w-3.5 h-3.5 text-muted-foreground/50" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-1">
                <span className="text-xs font-medium text-foreground truncate flex items-center gap-1">
                  {item.name}
                  {item.equipped && <Shield className="w-3 h-3 text-primary/70 shrink-0" />}
                  {item.is_quest_item && <Zap className="w-3 h-3 text-yellow-500/70 shrink-0" />}
                </span>
                <span className="flex items-center gap-1.5 shrink-0">
                  {item.quantity != null && item.quantity > 1 && (
                    <span className="text-[10px] text-muted-foreground">×{item.quantity}</span>
                  )}
                  {item.weight != null && (
                    <span className="text-[10px] text-muted-foreground">{item.weight}w</span>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                {item.category && (
                  <span className="text-[10px] text-muted-foreground capitalize">{item.category}</span>
                )}
                {item.equipped && item.equip_slot && (
                  <span className="text-[9px] text-primary/60 capitalize">· {item.equip_slot}</span>
                )}
                {hasModifiers && item.modifiers!.slice(0, 2).map((m, i) => (
                  <ModifierBadge key={i} mod={m} />
                ))}
              </div>
            </div>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 space-y-2" side="left">
        {item.image_url && (
          <img
            src={item.image_url}
            alt={item.name}
            className={cn("w-full h-28 object-cover rounded-md border", style.border)}
            loading="lazy"
            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
        )}
        <div className="font-medium text-sm text-foreground">{item.name}</div>
        <div className={cn("text-[10px] font-medium uppercase tracking-wider", style.text)}>
          {t[style.labelKey]}{item.category ? ` · ${item.category}` : ""}{item.equip_slot ? ` · ${item.equip_slot}` : ""}
        </div>

        {item.equipped && (
          <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">
            <Shield className="w-3 h-3 mr-1" /> {t.play_item_equipped}
          </Badge>
        )}

        {item.description && <p className="text-xs text-muted-foreground">{item.description}</p>}

        {/* Modifiers */}
        {hasModifiers && (
          <div className="space-y-1">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              {t.play_item_modifiers} {!item.equipped && t.play_item_when_equipped}
            </div>
            <div className="flex flex-wrap gap-1">
              {item.modifiers!.map((m, i) => (
                <ModifierBadge key={i} mod={m} />
              ))}
            </div>
          </div>
        )}

        {/* Effects */}
        {hasEffects && (
          <div className="space-y-1">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              {t.play_item_effects} {!item.equipped && t.play_item_when_equipped}
            </div>
            {item.effects!.map((eff, i) => (
              <div key={i} className="text-[10px] text-accent-foreground flex items-start gap-1">
                <Zap className="w-3 h-3 text-yellow-500/70 mt-0.5 shrink-0" />
                {eff}
              </div>
            ))}
          </div>
        )}

        {/* Durability */}
        {item.durability != null && (
          <div className="space-y-0.5">
            <div className="text-[10px] text-muted-foreground">{t.play_item_durability}: {item.durability}%</div>
            <div className="h-1 w-full rounded-full bg-muted/50">
              <div
                className={cn("h-full rounded-full transition-all", item.durability > 50 ? "bg-primary" : item.durability > 20 ? "bg-yellow-500" : "bg-destructive")}
                style={{ width: `${item.durability}%` }}
              />
            </div>
          </div>
        )}

        {/* Charges */}
        {hasCharges && (
          <div className="text-[10px] text-muted-foreground">
            {t.play_item_charges}: {item.charges}/{item.max_charges}
          </div>
        )}

        {item.weight != null && <p className="text-xs text-muted-foreground">{t.play_item_weight}: {item.weight}</p>}
        {item.is_cursed && <Badge variant="destructive" className="text-[10px]">{t.play_item_cursed}</Badge>}
        {item.is_quest_item && <Badge variant="outline" className="text-[10px] border-yellow-500/30 text-yellow-500">{t.play_item_quest}</Badge>}
      </PopoverContent>
    </Popover>
  );
}
